import { useCallback, useEffect, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export const BeforeAfter = ({ before, after, orientation = "landscape", testId = "before-after" }) => {
  const { t } = useI18n();
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const boxRef = useRef(null);

  const move = useCallback((clientX) => {
    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, next)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      if (e.touches) e.preventDefault();
      move(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const stop = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
    };
  }, [dragging, move]);

  return (
    <div
      ref={boxRef}
      data-testid={testId}
      className={`relative w-full select-none overflow-hidden border border-[#C69A3B]/40 bg-[#252525] ${
        orientation === "portrait" ? "aspect-[3/4]" : orientation === "square" ? "aspect-square" : "aspect-[4/3]"
      }`}
      onMouseDown={(e) => { setDragging(true); move(e.clientX); }}
    >
      <img src={after} alt={t("restoration.after")} loading="lazy" draggable={false}
        className="absolute inset-0 h-full w-full object-contain" />
      <img src={before} alt={t("restoration.before")} loading="lazy" draggable={false}
        className="absolute inset-0 h-full w-full object-contain"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />


      <span className="pointer-events-none absolute left-3 top-3 bg-[#252525]/80 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#FAFAF7]">
        {t("restoration.before")}
      </span>
      <span className="pointer-events-none absolute right-3 top-3 bg-[#8F3329]/90 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#FAFAF7]">
        {t("restoration.after")}
      </span>

      <div className="pointer-events-none absolute inset-y-0 w-[2px] bg-[#C69A3B]" style={{ left: `${pos}%` }}>
        <span
          data-testid={`${testId}-handle`}
          onTouchStart={(e) => { e.stopPropagation(); setDragging(true); }}
          style={{ touchAction: "none" }}
          className="pointer-events-auto absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-[#C69A3B] bg-[#FAFAF7] text-[#8F3329] shadow-lg"
        >
          <MoveHorizontal size={18} />
        </span>
      </div>

      <input
        type="range"
        min="0"
        max="100"
        value={Math.round(pos)}
        aria-label={t("restoration.drag")}
        data-testid={`${testId}-range`}
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute bottom-2 left-1/2 w-[70%] -translate-x-1/2 opacity-0"
      />
    </div>
  );
};

export default BeforeAfter;
